import React, { useState, useEffect } from "react";
import Button from "../common/Button";
import Icon from "../common/Icon";
import "./ConfirmDuplicatesModal.css";
import "./ConfirmGeocodingModal.css";

const formatGeocodingError = (error) => {
  if (error === "http_error") return "Erreur de géocodage, réessaie dans quelques secondes";
  if (error === "country_mismatch") return "Adresse hors du pays attendu";
  return "Adresse introuvable";
};

const ConfirmGeocodingModal = ({
  isOpen,
  results,
  expectedCountryCode,
  onConfirm,
  onCancel,
  onRecodeCorrections,
  recodingInputs = [],
  isLoading,
  loadingCount,
  loadingProgress,
  keywordsByLocation = {},
}) => {
  const [expandedInputs, setExpandedInputs] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [edits, setEdits] = useState({});
  const [editingInputs, setEditingInputs] = useState({});

  useEffect(() => {
    if (isLoading) {
      setSearchTerm("");
      setEdits({});
      setEditingInputs({});
    }
  }, [isLoading]);

  if (!isOpen) return null;

  const toggleExpanded = (input) => {
    setExpandedInputs((prev) => ({ ...prev, [input]: !prev[input] }));
  };

  const startEditing = (input) => {
    setEditingInputs((prev) => ({ ...prev, [input]: true }));
  };

  const handleEditChange = (input, value) => {
    setEdits((prev) => ({ ...prev, [input]: value }));
  };

  const buildCorrections = () => {
    const corrections = {};
    Object.entries(edits).forEach(([original, edited]) => {
      const trimmed = edited.trim();
      if (trimmed.length > 0 && trimmed !== original) {
        corrections[original] = trimmed;
      }
    });
    return corrections;
  };

  const handleRecodeClick = () => {
    onRecodeCorrections(buildCorrections());
    setEdits({});
    setEditingInputs({});
  };

  const getKeywordsForResult = (result) => {
    return keywordsByLocation[result.input.toLowerCase()] || [];
  };

  const isResolvable = (result) => {
    if (!result.success) return false;
    if (!result.countryCode) return true;
    return result.countryCode.toLowerCase() === expectedCountryCode.toLowerCase();
  };

  const totalResolvable = results.filter(isResolvable).length;
  const blockedLines = results.filter((r) => !isResolvable(r));
  const canConfirm = totalResolvable > 0 && blockedLines.length === 0;
  const pendingCorrections = buildCorrections();
  const pendingCorrectionCount = Object.keys(pendingCorrections).length;
  const recodingSet = new Set(recodingInputs.map((i) => i.toLowerCase()));
  const isRecoding = recodingSet.size > 0;

  const sortedResults = [...results].sort((a, b) => {
    const aBlocked = isResolvable(a) ? 1 : 0;
    const bBlocked = isResolvable(b) ? 1 : 0;
    return aBlocked - bBlocked;
  });

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const displayedResults = normalizedSearch
    ? sortedResults.filter((r) => {
        const inInput = r.input.toLowerCase().includes(normalizedSearch);
        const inAddress = r.formattedAddress?.toLowerCase().includes(normalizedSearch);
        return inInput || inAddress;
      })
    : sortedResults;

  return (
    <div className="confirm-modal-overlay" onClick={onCancel}>
      <div
        className="confirm-modal-content geocoding-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-modal-header">
          <div className="confirm-modal-icon warning">
            <Icon name="MapPin" size={32} />
          </div>
          <h3>Confirmation des géolocalisations</h3>
        </div>

        <div className="confirm-modal-body geocoding-modal-body">
          {isLoading ? (
            <div className="geocoding-loader">
              <span className="geocoding-spinner">
                <Icon name="Loader" size={20} />
              </span>
              <div className="geocoding-loader-text">
                <span>
                  {loadingCount > 0
                    ? `Résolution des adresses : ${loadingProgress || 0}/${loadingCount}`
                    : 'Résolution en cours...'}
                </span>
                {loadingCount > 0 && (
                  <>
                    <span className="geocoding-loader-bar">
                      <span
                        className="geocoding-loader-bar-fill"
                        style={{ width: `${Math.round(((loadingProgress || 0) / loadingCount) * 100)}%` }}
                      />
                    </span>
                    <span className="geocoding-loader-hint">
                      Géocodage des adresses en cours. Merci de patienter.
                    </span>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <p>
                Vérifie les adresses résolues ci-dessous.
                {blockedLines.length > 0 ? (
                  <>
                    {" "}
                    <strong>{blockedLines.length} ligne{blockedLines.length > 1 ? 's' : ''} en rouge</strong>
                    {' '}n{blockedLines.length > 1 ? 'ont' : "'a"} pas pu être géolocalisée{blockedLines.length > 1 ? 's' : ''} : clique « Corriger », édite, puis « Relancer » avant de lancer.
                  </>
                ) : (
                  " Tout est bon, tu peux lancer l'étude."
                )}
              </p>

              {results.length > 5 && (
                <div className="geocoding-search">
                  <Icon name="Search" size={16} />
                  <input
                    type="text"
                    className="geocoding-search-input"
                    placeholder="Rechercher une ville ou adresse..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="geocoding-search-clear"
                      onClick={() => setSearchTerm("")}
                    >
                      <Icon name="X" size={14} />
                    </button>
                  )}
                </div>
              )}

              <div className="geocoding-results-list">
                {displayedResults.length === 0 && (
                  <div className="geocoding-no-match">Aucune adresse ne correspond à « {searchTerm} »</div>
                )}
                {displayedResults.map((result) => {
                  const resolvable = isResolvable(result);
                  const associatedKeywords = getKeywordsForResult(result);
                  const isExpanded = !!expandedInputs[result.input];
                  const isRowRecoding = recodingSet.has(result.input.toLowerCase());
                  return (
                    <div
                      key={result.input}
                      className={`geocoding-result-row ${resolvable ? '' : 'blocked'} ${isRowRecoding ? 'recoding' : ''}`}
                    >
                      <div className="geocoding-result-header">
                        {isRowRecoding ? (
                          <span className="geocoding-status-dot recoding">
                            <span className="geocoding-spinner">
                              <Icon name="Loader" size={14} />
                            </span>
                          </span>
                        ) : (
                          <span className={`geocoding-status-dot ${resolvable ? 'ok' : 'ko'}`}>
                            <Icon name={resolvable ? "Check" : "AlertCircle"} size={14} />
                          </span>
                        )}
                        <div className="geocoding-result-info">
                          <div className="geocoding-input-label">
                            <Icon name="MapPin" size={14} />
                            {resolvable && <span>{result.input}</span>}
                            {!resolvable && editingInputs[result.input] && (
                              <input
                                type="text"
                                className="geocoding-edit-input"
                                value={edits[result.input] ?? result.input}
                                onChange={(e) => handleEditChange(result.input, e.target.value)}
                                placeholder="Corrige l'adresse..."
                                disabled={isRecoding}
                                autoFocus
                              />
                            )}
                            {!resolvable && !editingInputs[result.input] && (
                              <>
                                <span>{result.input}</span>
                                <button
                                  type="button"
                                  className="geocoding-correct-btn"
                                  onClick={() => startEditing(result.input)}
                                  disabled={isRecoding}
                                >
                                  <Icon name="Pencil" size={13} /> Corriger
                                </button>
                              </>
                            )}
                          </div>
                          {result.formattedAddress ? (
                            <div className="geocoding-resolved">
                              → {result.formattedAddress}
                              {result.latitude != null && result.longitude != null && (
                                <span className="geocoding-coords">
                                  ({Number(result.latitude).toFixed(4)}, {Number(result.longitude).toFixed(4)})
                                </span>
                              )}
                              {!resolvable && result.error === "country_mismatch" && result.countryCode && (
                                <span className="geocoding-conflict">
                                  {`Pays détecté : ${result.countryCode.toUpperCase()}, attendu : ${expectedCountryCode.toUpperCase()}`}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="geocoding-error">
                              {formatGeocodingError(result.error)}
                            </div>
                          )}
                        </div>
                        {associatedKeywords.length > 0 && (
                          <button
                            type="button"
                            className="geocoding-expand-btn"
                            onClick={() => toggleExpanded(result.input)}
                          >
                            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={14} />
                            {`${associatedKeywords.length} mot${associatedKeywords.length > 1 ? 's' : ''}-clé${associatedKeywords.length > 1 ? 's' : ''}`}
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <ul className="geocoding-keywords-sublist">
                          {associatedKeywords.map((keyword, keywordIndex) => (
                            <li key={keywordIndex}>{keyword}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="confirm-modal-question">
                {`${totalResolvable} adresse${totalResolvable > 1 ? 's' : ''} résolue${totalResolvable > 1 ? 's' : ''} sur ${results.length} saisie${results.length > 1 ? 's' : ''}`}
                {blockedLines.length > 0 && (
                  <span className="geocoding-blocked-count">
                    {` · ${blockedLines.length} à corriger`}
                  </span>
                )}
              </p>
            </>
          )}
        </div>

        <div className="confirm-modal-footer">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            icon={<Icon name="ArrowLeft" size={18} />}
            disabled={isLoading || isRecoding}
          >
            Retour
          </Button>
          {pendingCorrectionCount > 0 && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleRecodeClick}
              icon={<Icon name="RefreshCw" size={18} />}
              disabled={isLoading || isRecoding}
            >
              {isRecoding
                ? "Relance en cours..."
                : `Relancer ${pendingCorrectionCount} corrigée${pendingCorrectionCount > 1 ? 's' : ''}`}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            icon={<Icon name="Check" size={18} />}
            disabled={isLoading || isRecoding || !canConfirm}
          >
            Confirmer et lancer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmGeocodingModal;
